import { useCallback } from 'react';
import { printSize } from '@teetsh/app/src/utils/printSize';
import { Attachment } from '@teetsh/app/src/types/attachment';
import { Accept, useDropzone as useDZ } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

interface Props {
  onChange: (v: Attachment[]) => void;
  attachments?: Attachment[];
  handleError: ({ message }: { message: string }) => void;
  handleUploadAttachment: (payload: { file: File }) => Promise<any>;
  multiple?: boolean;
  acceptOnly?: AcceptOnly;
  disabled?: boolean;
}

const MAX_FILE_SIZE = 10000000;

export type AcceptOnly = 'csv' | 'images';
export const AcceptedTypes: Record<AcceptOnly, Accept> = {
  csv: {
    'text/csv': ['.csv'],
  },
  images: {
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png'],
    'image/gif': ['.gif'],
    'image/webp': ['.webp'],
    'image/svg+xml': ['.svg'],
  },
};

export function useDropzone(props: Props) {
  const {
    handleUploadAttachment,
    handleError,
    attachments = [],
    onChange,
    multiple = false,
    acceptOnly,
    disabled,
  } = props;
  const { t } = useTranslation(['common']);
  const onDrop = useCallback(
    (acceptedFiles, rejectedFiles) => {
      Promise.all(
        acceptedFiles.map(file => handleUploadAttachment({ file })),
      ).then(allData => {
        const data = allData.map(d => (d?.attachment ? d.attachment : d));
        onChange([...attachments, ...data]);
      });
      rejectedFiles.forEach(file => {
        handleError({
          message: t('common:components.dropzone.fileSizeError', {
            name: file.name,
            size: printSize(file.size, t('common:fileSizeLocale')),
            maxSize: printSize(MAX_FILE_SIZE, t('common:fileSizeLocale')),
          }),
        });
      });
    },
    [attachments, onChange, handleUploadAttachment, handleError, t],
  );
  const accept = acceptOnly && AcceptedTypes[acceptOnly];

  return useDZ({
    disabled,
    onDrop,
    maxSize: MAX_FILE_SIZE,
    multiple,
    accept,
  });
}
